import { type FormEvent, useEffect, useState } from "react";
import { fetchMedia, fetchMediaPlaybackUrl, type MediaItem, uploadMedia } from "./media";
import { GooglePhotosImporter } from "./GooglePhotosImporter";

const MAX_UPLOAD_BYTES = 30 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface MediaLibraryProps {
  onLibraryChanged?: () => void;
}

export function MediaLibrary({ onLibraryChanged }: MediaLibraryProps = {}) {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState("Loading library…");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<{ item: MediaItem; url: string } | null>(null);

  useEffect(() => {
    void fetchMedia()
      .then((media) => {
        setItems(media);
        setStatus(media.length === 0 ? "No broadcasts yet." : "");
      })
      .catch((error: unknown) => setStatus(error instanceof Error ? error.message : "Could not load library"));
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!file || !title.trim()) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setStatus("Choose a file smaller than 30 MB.");
      return;
    }

    setUploading(true);
    setStatus("Uploading…");
    try {
      const item = await uploadMedia(file, title.trim());
      setItems((current) => [item, ...current]);
      setTitle("");
      setFile(null);
      setStatus("Added to the private library.");
      onLibraryChanged?.();
      form.reset();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  // The Library listed titles and byte counts but never rendered the media, so
  // there was nothing to view in the Studio at all — separate from the delivery
  // bug that made /api/media/{id}/content answer 401. Playback goes through the
  // same short-lived session as Cat TV; no permanent download URLs.
  async function showPreview(item: MediaItem) {
    setStatus("Opening preview…");
    try {
      setPreview({ item, url: await fetchMediaPlaybackUrl(item) });
      setStatus("");
    } catch (error) {
      setPreview(null);
      setStatus(error instanceof Error ? error.message : "Could not open preview");
    }
  }

  return (
    <section className="library-panel" aria-labelledby="library-heading">
      <div className="library-heading">
        <div>
          <p className="eyebrow">01 · Source material</p>
          <h2 id="library-heading">Library</h2>
        </div>
        <span>{items.length} {items.length === 1 ? "item" : "items"}</span>
      </div>

      <form className="upload-form" onSubmit={(event) => void handleSubmit(event)}>
        <label>
          <span>Title</span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} required placeholder="Window nap" />
        </label>
        <label>
          <span>Photo or video</span>
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm" required onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
        </label>
        <button type="submit" disabled={uploading}>{uploading ? "Uploading…" : "Add to library"}</button>
      </form>

      <GooglePhotosImporter onImported={(imported) => {
        setItems((current) => [...imported, ...current]);
        if (imported.length > 0) onLibraryChanged?.();
      }} />

      {status && <p className="library-status" role="status">{status}</p>}
      {preview && (
        <figure className="player">
          {preview.item.content_type.startsWith("video/") ? (
            <video src={preview.url} controls playsInline />
          ) : (
            <img src={preview.url} alt={preview.item.title} />
          )}
          <figcaption>
            {preview.item.title}
            <button type="button" onClick={() => setPreview(null)}>Close preview</button>
          </figcaption>
        </figure>
      )}
      {items.length > 0 && (
        <div className="media-list">
          {items.map((item) => (
            <article key={item.id}>
              <span className="media-kind">{item.content_type.startsWith("video/") ? "Movie" : "Photo"}</span>
              <div><h3>{item.title}</h3><p>{item.filename}</p></div>
              <div className="media-list-actions">
                <span>{formatSize(item.size_bytes)}</span>
                <button type="button" onClick={() => void showPreview(item)}>Preview</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
